import React from 'react';
import type { MineralogyBlock, MineralEntry } from '../../lib/api/geologist';
import type { GeoUiLang } from './geoWorkspaceI18n';

// P7.4.1 — showpiece mineralogy answer. Grounded in the source: qualitative mineral
// roles (main/impurity) kept separate from the quantified chemistry chart.

const Crystal: React.FC<{ color: string; size?: number }> = ({ color, size = 34 }) => (
  <svg width={size} height={size} viewBox="0 0 40 44" aria-hidden>
    <defs>
      <linearGradient id={`g-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#ffffff" stopOpacity="0.55" />
        <stop offset="0.35" stopColor={color} />
        <stop offset="1" stopColor={color} stopOpacity="0.82" />
      </linearGradient>
    </defs>
    <polygon points="20,2 33,11 33,29 20,42 7,29 7,11" fill={`url(#g-${color.replace('#', '')})`}
      stroke={color} strokeWidth="1" />
    <polyline points="20,2 20,42" stroke="#ffffff" strokeOpacity="0.35" strokeWidth="0.8" fill="none" />
    <polyline points="7,11 20,18 33,11" stroke="#ffffff" strokeOpacity="0.4" strokeWidth="0.8" fill="none" />
    <polyline points="7,29 20,22 33,29" stroke="#000000" strokeOpacity="0.12" strokeWidth="0.8" fill="none" />
  </svg>
);

const MainMineral: React.FC<{ m: MineralEntry; label: string }> = ({ m, label }) => (
  <div style={{ display: 'flex', gap: 14, alignItems: 'center', padding: 16, borderRadius: 12,
    background: `linear-gradient(135deg, ${m.color}14, ${m.color}05)`, border: `1px solid ${m.color}44` }}>
    <Crystal color={m.color} size={54} />
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: m.color }}>
        {label}
      </div>
      <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--ink-primary)' }}>{m.name_ru}</div>
      {m.formula && <div style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--ink-secondary)', marginTop: 2 }}>{m.formula}</div>}
    </div>
  </div>
);

const ImpurityChip: React.FC<{ m: MineralEntry }> = ({ m }) => (
  <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 12px', borderRadius: 10,
    border: '1px solid var(--border)', background: 'var(--bg-card)' }}>
    <Crystal color={m.color} size={26} />
    <div>
      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink-primary)' }}>{m.name_ru}</div>
      {m.formula && <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-tertiary)' }}>{m.formula}</div>}
    </div>
  </div>
);

const MineralogyPanel: React.FC<{ b: MineralogyBlock; lang?: GeoUiLang }> = ({ b, lang = 'ru' }) => {
  const copy = {
    ru: { main: 'Главный минерал', impurities: 'Примеси', chemistry: 'Химический состав', mass: '% масс.', genesis: 'Генезис', source: 'Источник' },
    hy: { main: 'Հիմնական միներալ', impurities: 'Խառնուրդներ', chemistry: 'Քիմիական կազմ', mass: 'զանգվ. %', genesis: 'Ծագում', source: 'Աղբյուր' },
    en: { main: 'Main mineral', impurities: 'Impurities', chemistry: 'Chemical composition', mass: 'wt %', genesis: 'Genesis', source: 'Source' },
  }[lang];
  const main = b.minerals.filter((m) => m.role === 'main');
  const imp = b.minerals.filter((m) => m.role !== 'main');
  const maxBar = Math.max(1, ...b.chemistry_bars.map((x) => x.value));

  return (
    <div data-testid="preview-mineralogy" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)',
      borderRadius: 12, padding: 20, marginBottom: 16, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink-primary)' }}>{b.title}</div>
        {b.material_ru && <span className="pill pill-green">{b.material_ru}</span>}
        {b.deposit_ru && <span style={{ fontSize: 13, color: 'var(--ink-tertiary)' }}>{b.deposit_ru}</span>}
      </div>

      {main.map((m) => <div key={m.name_ru} style={{ marginTop: 14 }}><MainMineral m={m} label={copy.main} /></div>)}

      {imp.length > 0 && (
        <>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
            color: 'var(--ink-tertiary)', margin: '16px 0 8px' }}>{copy.impurities}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {imp.map((m) => <ImpurityChip key={m.name_ru} m={m} />)}
          </div>
        </>
      )}

      {b.chemistry_bars.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
            color: 'var(--ink-tertiary)', marginBottom: 10 }}>
            {copy.chemistry}{b.chemistry_sample ? ` · ${b.chemistry_sample}` : ''} <span style={{ fontWeight: 400 }}>({copy.mass})</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {b.chemistry_bars.map((bar) => (
              <div key={bar.label} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 62, fontSize: 13, color: 'var(--ink-secondary)', textAlign: 'right',
                  fontFamily: 'var(--font-mono)' }}>{bar.label}</div>
                <div style={{ flex: 1, height: 20, background: 'var(--bg-subtle)', borderRadius: 5, overflow: 'hidden' }}>
                  <div style={{ width: `${(bar.value / maxBar) * 100}%`, height: '100%',
                    background: `linear-gradient(90deg, ${bar.color}, ${bar.color}bb)`, borderRadius: 5,
                    transition: 'width .5s ease' }} />
                </div>
                <div style={{ width: 52, fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)',
                  fontVariantNumeric: 'tabular-nums' }}>{bar.value}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {(b.host_rocks || b.rock_types) && (
        <div style={{ marginTop: 18, padding: 12, borderRadius: 10, background: 'var(--bg-card-inset)',
          borderLeft: '3px solid var(--steel)', fontSize: 13, lineHeight: 1.6, color: 'var(--ink-secondary)' }}>
          <div style={{ fontWeight: 600, color: 'var(--ink-primary)', marginBottom: 2 }}>{copy.genesis}</div>
          {b.host_rocks && <div>{b.host_rocks}</div>}
          {b.rock_types && <div style={{ marginTop: 4 }}>{b.rock_types}</div>}
        </div>
      )}

      {b.disclaimer && (
        <div style={{ marginTop: 12, fontSize: 12, color: 'var(--ink-tertiary)' }}>{b.disclaimer}</div>
      )}
      {b.evidence_refs.length > 0 && (
        <div style={{ marginTop: 6, fontSize: 12, color: 'var(--ink-tertiary)' }}>
          {copy.source}: {b.evidence_refs.join(', ')}
        </div>
      )}
    </div>
  );
};

export default MineralogyPanel;
